export const SOL_MARKET_TOKEN_ID = "sol";
export const LEGACY_SPL_TOKEN_PROGRAM_ID = "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA";
export const MARKET_CREATE_FEE_LAMPORTS = "250000000";

const U64_MAX = 18446744073709551615n;

const MARKET_PAYMENT_TOKENS = Object.freeze([
  Object.freeze({
    id: SOL_MARKET_TOKEN_ID,
    kind: "native",
    symbol: "SOL",
    name: "Solana",
    mint: null,
    tokenProgramId: null,
    decimals: 9,
    createDepositUnits: "0",
    marketCreateFeeLamports: MARKET_CREATE_FEE_LAMPORTS,
    voteAmountUnits: Object.freeze(["10000000", "50000000", "100000000", "250000000", "1000000000"]),
    voteAmountLabels: Object.freeze(["0.01", "0.05", "0.10", "0.25", "1.00"]),
    active: true,
    allowExistingMarketVotesWhenInactive: false
  }),
  Object.freeze({
    id: "skr",
    kind: "spl",
    symbol: "SKR",
    name: "SKR",
    mint: "SKRbvo6Gf7GondiT3BbTfuRDPqLWei4j2Qy2NPGZhW3",
    tokenProgramId: LEGACY_SPL_TOKEN_PROGRAM_ID,
    decimals: 6,
    createDepositUnits: "1000000",
    marketCreateFeeLamports: MARKET_CREATE_FEE_LAMPORTS,
    voteAmountUnits: Object.freeze(["1000000", "2000000", "3000000", "4000000", "5000000"]),
    voteAmountLabels: Object.freeze(["1", "2", "3", "4", "5"]),
    active: false,
    allowExistingMarketVotesWhenInactive: false
  })
]);

export const MARKET_PAYMENT_TOKEN_IDS = Object.freeze(MARKET_PAYMENT_TOKENS.map((token) => token.id));

export function getMarketPaymentTokenConfig(tokenId) {
  const id = String(tokenId || SOL_MARKET_TOKEN_ID).trim().toLowerCase();
  const token = MARKET_PAYMENT_TOKENS.find((entry) => entry.id === id);
  if (!token) {
    throw new Error(`Unsupported market payment token: ${id || "(empty)"}.`);
  }
  return cloneTokenConfig(token);
}

export function getDefaultMarketPaymentTokenConfig() {
  return getMarketPaymentTokenConfig(SOL_MARKET_TOKEN_ID);
}

export function assertActiveMarketPaymentToken(tokenId) {
  const tokenConfig = getMarketPaymentTokenConfig(tokenId);
  if (tokenConfig.active !== true) {
    throw new Error(`Market payment token ${tokenConfig.id} is inactive.`);
  }
  return snapshotMarketPaymentTokenConfig(tokenConfig);
}

export function snapshotMarketPaymentTokenConfig(tokenConfig) {
  validateMarketPaymentTokenConfig(tokenConfig);
  return cloneTokenConfig({
    id: normalizeTokenId(tokenConfig.id),
    kind: normalizeTokenKind(tokenConfig.kind),
    symbol: normalizeRequiredText(tokenConfig.symbol, "symbol"),
    name: normalizeRequiredText(tokenConfig.name || tokenConfig.symbol, "name"),
    mint: tokenConfig.mint == null ? null : normalizeRequiredText(tokenConfig.mint, "mint"),
    tokenProgramId: tokenConfig.tokenProgramId == null ? null : normalizeRequiredText(tokenConfig.tokenProgramId, "tokenProgramId"),
    decimals: tokenConfig.decimals == null ? null : normalizeDecimals(tokenConfig.decimals),
    createDepositUnits: tokenConfig.createDepositUnits == null ? null : assertTokenUnitsFitU64(tokenConfig.createDepositUnits),
    marketCreateFeeLamports: assertTokenUnitsFitU64(tokenConfig.marketCreateFeeLamports ?? MARKET_CREATE_FEE_LAMPORTS),
    voteAmountUnits: normalizeTokenUnitList(tokenConfig.voteAmountUnits || []),
    voteAmountLabels: normalizeLabelList(tokenConfig.voteAmountLabels || []),
    active: tokenConfig.active === true,
    allowExistingMarketVotesWhenInactive: tokenConfig.allowExistingMarketVotesWhenInactive === true
  });
}

export function isSupportedVoteAmountUnits(tokenConfig, units) {
  try {
    const normalized = assertTokenUnitsFitU64(units);
    const voteAmountUnits = normalizeTokenUnitList(tokenConfig?.voteAmountUnits || []);
    return voteAmountUnits.includes(normalized);
  } catch {
    return false;
  }
}

export function parseTokenUiAmountToUnits(input, decimals) {
  const normalizedDecimals = normalizeDecimals(decimals);
  const raw = String(input ?? "").trim();
  if (!raw) {
    throw new Error("Token amount is required.");
  }
  if (!/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(raw)) {
    throw new Error("Token amount must be a non-negative decimal string.");
  }

  const [wholePartRaw, fractionalPartRaw = ""] = raw.split(".");
  const wholePart = wholePartRaw || "0";
  const fractionalPart = fractionalPartRaw || "";
  if (fractionalPart.length > normalizedDecimals) {
    throw new Error("Token amount has too many decimal places.");
  }

  const scale = 10n ** BigInt(normalizedDecimals);
  const wholeUnits = BigInt(normalizeIntegerDigits(wholePart)) * scale;
  const fractionalPadded = fractionalPart.padEnd(normalizedDecimals, "0") || "0";
  const fractionalUnits = BigInt(normalizeIntegerDigits(fractionalPadded));
  return assertTokenUnitsFitU64(wholeUnits + fractionalUnits);
}

export function formatTokenUnits(units, decimals) {
  const normalizedDecimals = normalizeDecimals(decimals);
  const value = BigInt(assertTokenUnitsFitU64(units));
  if (normalizedDecimals === 0) return value.toString();

  const scale = 10n ** BigInt(normalizedDecimals);
  const whole = value / scale;
  const fractional = value % scale;
  if (fractional === 0n) return whole.toString();

  const fractionalText = fractional
    .toString()
    .padStart(normalizedDecimals, "0")
    .replace(/0+$/, "");
  return `${whole}.${fractionalText}`;
}

export function assertTokenUnitsFitU64(units) {
  const normalized = normalizeUnsignedIntegerString(units);
  if (BigInt(normalized) > U64_MAX) {
    throw new Error("Token unit value exceeds u64 range.");
  }
  return normalized;
}

export function assertLegacyTokenProgramForV1(tokenConfig) {
  const tokenProgramId = String(tokenConfig?.tokenProgramId || "").trim();
  if (tokenProgramId !== LEGACY_SPL_TOKEN_PROGRAM_ID) {
    throw new Error("V1 SPL markets require the legacy SPL Token Program.");
  }
  return tokenProgramId;
}

function validateMarketPaymentTokenConfig(tokenConfig) {
  if (!tokenConfig || typeof tokenConfig !== "object") {
    throw new Error("Market payment token config is required.");
  }

  const id = normalizeTokenId(tokenConfig.id);
  const kind = normalizeTokenKind(tokenConfig.kind);
  if (kind === "native") {
    validateNativeTokenConfig({ ...tokenConfig, id });
    return;
  }
  validateSplTokenConfig({ ...tokenConfig, id });
}

function validateNativeTokenConfig(tokenConfig) {
  if (tokenConfig.id !== SOL_MARKET_TOKEN_ID) {
    throw new Error("Only SOL is supported as the native market payment token.");
  }
  if (tokenConfig.mint != null || tokenConfig.tokenProgramId != null) {
    throw new Error("Native SOL market payment token must not include mint or token program metadata.");
  }
  normalizeDecimals(tokenConfig.decimals);
  normalizeTokenUnitList(tokenConfig.voteAmountUnits || []);
  normalizeLabelList(tokenConfig.voteAmountLabels || []);
  if (tokenConfig.active === true) {
    assertExactlyFive(tokenConfig.voteAmountUnits, "voteAmountUnits");
    assertExactlyFive(tokenConfig.voteAmountLabels, "voteAmountLabels");
  }
}

function validateSplTokenConfig(tokenConfig) {
  if (tokenConfig.active !== true) return;

  normalizeRequiredText(tokenConfig.mint, "mint");
  normalizeRequiredText(tokenConfig.tokenProgramId, "tokenProgramId");
  normalizeDecimals(tokenConfig.decimals);
  assertTokenUnitsFitU64(tokenConfig.createDepositUnits);
  assertExactlyFive(tokenConfig.voteAmountUnits, "voteAmountUnits");
  assertExactlyFive(tokenConfig.voteAmountLabels, "voteAmountLabels");
  normalizeTokenUnitList(tokenConfig.voteAmountUnits);
  normalizeLabelList(tokenConfig.voteAmountLabels);
  assertLegacyTokenProgramForV1(tokenConfig);
}

function assertExactlyFive(value, fieldName) {
  if (!Array.isArray(value) || value.length !== 5) {
    throw new Error(`Active SPL market payment tokens must configure exactly five ${fieldName}.`);
  }
}

function normalizeTokenUnitList(value) {
  if (!Array.isArray(value)) {
    throw new Error("Token amount units must be an array.");
  }
  return value.map((units) => assertTokenUnitsFitU64(units));
}

function normalizeLabelList(value) {
  if (!Array.isArray(value)) {
    throw new Error("Token amount labels must be an array.");
  }
  return value.map((label) => normalizeRequiredText(label, "voteAmountLabels"));
}

function normalizeDecimals(decimals) {
  if (!Number.isInteger(Number(decimals))) {
    throw new Error("Token decimals must be an integer.");
  }
  const normalized = Number(decimals);
  if (normalized < 0 || normalized > 255) {
    throw new Error("Token decimals must be between 0 and 255.");
  }
  return normalized;
}

function normalizeTokenKind(kind) {
  const normalized = String(kind || "").trim().toLowerCase();
  if (normalized !== "native" && normalized !== "spl") {
    throw new Error("Market payment token kind must be native or spl.");
  }
  return normalized;
}

function normalizeTokenId(id) {
  const normalized = String(id || "").trim().toLowerCase();
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(normalized)) {
    throw new Error("Market payment token id is invalid.");
  }
  return normalized;
}

function normalizeRequiredText(value, fieldName) {
  const text = String(value || "").trim();
  if (!text) {
    throw new Error(`Market payment token ${fieldName} is required.`);
  }
  return text;
}

function normalizeUnsignedIntegerString(value) {
  const raw = typeof value === "bigint" ? value.toString() : String(value ?? "").trim();
  if (!/^\d+$/.test(raw)) {
    throw new Error("Token unit value must be an unsigned integer string.");
  }
  return normalizeIntegerDigits(raw);
}

function normalizeIntegerDigits(value) {
  return String(value || "0").replace(/^0+(?=\d)/, "") || "0";
}

function cloneTokenConfig(tokenConfig) {
  return {
    ...tokenConfig,
    voteAmountUnits: [...(tokenConfig.voteAmountUnits || [])],
    voteAmountLabels: [...(tokenConfig.voteAmountLabels || [])]
  };
}
